import { brand } from '@alison/ui';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import { ChartsTooltipContainer, useAxesTooltip } from '@mui/x-charts/ChartsTooltip';
import { useDrawingArea } from '@mui/x-charts/hooks';
import type { ReactNode } from 'react';
import { rankedBarGradient } from '../data/marketColors';
import type { MarketRow } from '../data/marketPerformanceData';
import { formatMarketMillions, formatMarketRate, formatMarketThousands } from '../data/utils';

function Legend({ color, label }: { color: string; label: string }): ReactNode {
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 11, color: 'text.secondary' }}
    >
      <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: color }} />
      {label}
    </Box>
  );
}

function TooltipRow({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value: string;
}): ReactNode {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ width: 9, height: 9, borderRadius: 0.5, bgcolor: color, flexShrink: 0 }} />
      <Box sx={{ flexGrow: 1, fontSize: 12, color: 'text.secondary' }}>{label}</Box>
      <Box sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary' }}>{value}</Box>
    </Box>
  );
}

function BarLabels({
  rows,
  yMax,
  currencySymbol,
  leaderColor,
}: {
  rows: MarketRow[];
  yMax: number;
  currencySymbol: string;
  leaderColor: (index: number) => string;
}): ReactNode {
  const { left, top, width, height } = useDrawingArea();
  const step = width / Math.max(1, rows.length);
  const baseY = top + height;
  const yFor = (value: number): number => baseY - (value / yMax) * height;

  return (
    <>
      {rows.map((market, index) => {
        const centerX = left + step * (index + 0.5);
        const spendTopY = yFor(market.spendMillions);
        return (
          <g key={market.code} textAnchor="middle" pointerEvents="none">
            <text
              x={centerX}
              y={spendTopY - 28}
              fill="var(--mui-palette-text-disabled)"
              fontSize={11}
              fontWeight={400}
            >
              SPEND
            </text>
            <text
              x={centerX}
              y={spendTopY - 8}
              fill="var(--mui-palette-text-primary)"
              fontSize={14}
              fontWeight={600}
            >
              {formatMarketMillions(market.spendMillions, currencySymbol)}
            </text>
            <text
              x={centerX}
              y={baseY + 40}
              fill={leaderColor(index)}
              fontSize={12}
              fontWeight={800}
            >
              {formatMarketRate(market.savingsRate)}
            </text>
            <text
              x={centerX}
              y={baseY + 55}
              fill="var(--mui-palette-text-secondary)"
              fontSize={11}
              fontWeight={700}
            >
              {formatMarketThousands(market.potentialSavingsK, currencySymbol)}
            </text>
          </g>
        );
      })}
    </>
  );
}

export function SpendVsSavings({
  markets,
  currencySymbol,
}: {
  markets: MarketRow[];
  currencySymbol: string;
}): ReactNode {
  const theme = useTheme();
  const rows = markets;
  const spendColor = theme.palette.border.default;

  const topPerformanceIndex = Math.max(...rows.map((market) => market.performanceIndex));
  const gradients = rows.map((market, index) =>
    rankedBarGradient(index, rows.length, market.performanceIndex === topPerformanceIndex),
  );
  const savings = rows.map((market) => (market.spendMillions * market.savingsRate) / 100);
  const remaining = rows.map((market, index) => market.spendMillions - (savings[index] ?? 0));
  const codes = rows.map((market) => market.code);
  const yMax = Math.max(...rows.map((market) => market.spendMillions)) * 1.2;

  const savingsSeries = rows.map((market, index) => {
    const value = savings[index] ?? 0;
    return {
      id: `savings-${market.code}`,
      label: 'Potential saving',
      stack: 'spend',
      color: gradients[index]?.[1] ?? brand.bgPurple,
      data: rows.map((_, position): number | null => (position === index ? value : null)),
    };
  });

  function SpendTooltip(): ReactNode {
    const axes = useAxesTooltip();
    const dataIndex = axes?.[0]?.dataIndex;
    const market = dataIndex === undefined ? undefined : rows[dataIndex];
    if (market === undefined || dataIndex === undefined) return null;
    return (
      <ChartsTooltipContainer trigger="axis">
        <Box
          sx={(theme) => ({
            minWidth: 200,
            p: 1.5,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: theme.palette.border.default,
            borderRadius: `${theme.radius.md}px`,
            boxShadow: theme.shadows[3],
          })}
        >
          <Box sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', mb: 1 }}>
            {market.market}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <TooltipRow
              color={spendColor}
              label="Total spend"
              value={formatMarketMillions(market.spendMillions, currencySymbol)}
            />
            <TooltipRow
              color={gradients[dataIndex]?.[1] ?? brand.bgPurple}
              label="Potential saving"
              value={`${formatMarketRate(market.savingsRate)} · ${formatMarketThousands(
                market.potentialSavingsK,
                currencySymbol,
              )}`}
            />
          </Box>
        </Box>
      </ChartsTooltipContainer>
    );
  }

  return (
    <Box>
      <BarChart
        height={340}
        series={[
          ...savingsSeries,
          {
            id: 'remaining',
            label: 'Total spend',
            data: remaining,
            stack: 'spend',
            color: spendColor,
          },
        ]}
        xAxis={[
          {
            data: codes,
            scaleType: 'band',
            categoryGapRatio: 0.65,
            disableLine: true,
            disableTicks: true,
            tickLabelStyle: { fontSize: 13, fontWeight: 600 },
          },
        ]}
        yAxis={[{ min: 0, max: yMax, width: 0, disableLine: true, disableTicks: true }]}
        borderRadius={5}
        hideLegend
        slots={{ tooltip: SpendTooltip }}
        margin={{ top: 8, right: 8, bottom: 64, left: 8 }}
      >
        <BarLabels
          rows={rows}
          yMax={yMax}
          currencySymbol={currencySymbol}
          leaderColor={(index) => gradients[index]?.[0] ?? spendColor}
        />
      </BarChart>
      <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center', mt: 1 }}>
        <Legend color={spendColor} label="Total spend" />
        <Legend color={brand.bgPurple} label="Potential saving" />
      </Box>
    </Box>
  );
}
